/** Nested editor controls own their wheel gestures, including gestures starting on SVG artwork. */
export function canvasNavigationBelongsToControl(target: EventTarget | null, viewport: Element): boolean {
  let element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  while (element && element !== viewport) {
    if (
      element.getAttribute(
        "data-canvas-navigation", // no itx
      ) === "ignore"
    )
      return true;
    const style = getComputedStyle(element);
    const scrolls = (overflow: string, content: number, visible: number) =>
      ["auto", "scroll", "overlay"].includes(overflow) && content > visible + 1;
    if (scrolls(style.overflowY, element.scrollHeight, element.clientHeight) ||
      scrolls(style.overflowX, element.scrollWidth, element.clientWidth)) return true;
    element = element.parentElement;
  }
  return false;
}

type EditorWheelEvent = Pick<WheelEvent, "target" | "currentTarget" | "deltaX" | "deltaY" | "deltaMode" | "ctrlKey" | "metaKey" | "preventDefault" | "stopPropagation">;

/** Route trackpad scrolling inside one editor panel, independently of native scroll chaining. */
export function scrollCanvasControl(event: EditorWheelEvent): void {
  const target = event.target instanceof Element ? event.target : event.target instanceof Node ? event.target.parentElement : null;
  // An embedded viewport owns its own zoom and pan gestures.
  const nestedViewport = target?.closest("[data-canvas-navigation-viewport]");
  if (
    nestedViewport?.getAttribute(
      "data-canvas-navigation-viewport", // no itx
    ) != null
  )
    return;
  event.preventDefault();
  event.stopPropagation();
  if (event.ctrlKey || event.metaKey) return;
  const root = event.currentTarget;
  if (!(root instanceof HTMLElement)) return;
  if (!target || !root.contains(target)) return;

  const nearestPanel = target.closest<HTMLElement>(".dockPanel");
  const boundary = nearestPanel && root.contains(nearestPanel) ? nearestPanel : root;
  const candidates: HTMLElement[] = [];
  const isScrollable = (element: Element): element is HTMLElement => {
    if (!(element instanceof HTMLElement)) return false;
    const style = getComputedStyle(element);
    return (["auto", "scroll", "overlay"].includes(style.overflowY) && element.scrollHeight > element.clientHeight + 1) ||
      (["auto", "scroll", "overlay"].includes(style.overflowX) && element.scrollWidth > element.clientWidth + 1);
  };
  for (let element: Element | null = target; element; element = element.parentElement) {
    if (isScrollable(element)) candidates.push(element);
    if (element === boundary) break;
  }
  // Headers are siblings of their scrollable content, rather than descendants of it.
  if (!candidates.length) candidates.push(...Array.from(boundary.querySelectorAll("*")).filter(isScrollable));

  const lineHeight = parseFloat(getComputedStyle(root).lineHeight) || 16;
  const scrollAxis = (axis: "x" | "y", delta: number) => {
    for (const element of candidates) {
      const style = getComputedStyle(element);
      if (!["auto", "scroll", "overlay"].includes(axis === "x" ? style.overflowX : style.overflowY)) continue;
      const extent = axis === "x" ? element.clientWidth : element.clientHeight;
      const limit = (axis === "x" ? element.scrollWidth : element.scrollHeight) - extent;
      const previous = axis === "x" ? element.scrollLeft : element.scrollTop;
      const pixels = delta * (event.deltaMode === 1 ? lineHeight : event.deltaMode === 2 ? extent : 1);
      const next = Math.max(0, Math.min(limit, previous + pixels));
      if (next === previous || limit <= 0) continue;
      if (axis === "x") element.scrollLeft = next;
      else element.scrollTop = next;
      break;
    }
  };
  scrollAxis("x", event.deltaX);
  scrollAxis("y", event.deltaY);
}
